import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, Loader2, MapPin, CalendarClock, DollarSign, Search, Building2, Users, Landmark } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/apiClient";

const AUD_ICON = { b2c: Users, b2b: Building2, b2g: Landmark };
const AUD_COLOR = { b2c: "bg-teal-100 text-teal-800", b2b: "bg-blue-100 text-blue-800", b2g: "bg-amber-100 text-amber-800" };

export default function Opportunities() {
  const [filters, setFilters] = useState(null);
  const [sel, setSel] = useState({ audience: "", state: "", type: "" });
  const [county, setCounty] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(sel).forEach(([k, v]) => { if (v) params.append(k, v); });
    if (county) params.append("county", county);
    api.get(`/opportunities?${params.toString()}`).then((r) => setItems(r.data.opportunities)).catch(() => {}).finally(() => setLoading(false));
  }, [sel, county]);

  useEffect(() => { api.get("/opportunities/filters").then((r) => setFilters(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const FilterSelect = ({ k, placeholder, items: opts, valueKey = "id" }) => (
    <Select value={sel[k] || "all"} onValueChange={(v) => setSel({ ...sel, [k]: v === "all" ? "" : v })}>
      <SelectTrigger data-testid={`opp-${k}`} className="bg-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72"><SelectItem value="all">{placeholder}</SelectItem>
        {opts?.map((it) => <SelectItem key={it[valueKey]} value={it[valueKey]}>{it.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div>
      <section className="bg-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-3 flex items-center gap-2"><Briefcase className="w-4 h-4" /> National Opportunity Marketplace</p>
              <h1 className="font-display text-4xl lg:text-5xl font-bold max-w-3xl leading-tight">Find care opportunities across B2C, B2B &amp; B2G</h1>
              <p className="mt-4 text-blue-100/80 max-w-2xl text-lg">Home-care clients, facility staffing, hospital & managed-care partnerships, Medicaid panels and government contracts — all in one board.</p>
            </div>
            <span className="text-[11px] font-semibold uppercase tracking-wide bg-white/10 text-blue-100 px-2.5 py-1 rounded-full self-start">Demo listings</span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          <aside className="space-y-3 h-fit lg:sticky lg:top-24" data-testid="opp-filters">
            {filters && <>
              <FilterSelect k="audience" placeholder="All audiences" items={filters.audiences} />
              <FilterSelect k="type" placeholder="All opportunity types" items={filters.types} />
              <FilterSelect k="state" placeholder="All states" items={filters.states} valueKey="slug" />
              <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" /><Input data-testid="opp-county" value={county} onChange={(e) => setCounty(e.target.value)} placeholder="Filter by county…" className="bg-white pl-9" /></div>
            </>}
            <Button variant="outline" onClick={() => { setSel({ audience: "", state: "", type: "" }); setCounty(""); }} data-testid="opp-reset" className="w-full">Reset filters</Button>
          </aside>

          <div>
            <p className="text-sm text-slate-500 mb-4">{loading ? "Loading…" : `${items.length} opportunit${items.length !== 1 ? "ies" : "y"}`}</p>
            {loading ? <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div> : (
              <div className="grid md:grid-cols-2 gap-5" data-testid="opp-grid">
                {items.map((o, i) => {
                  const Icon = AUD_ICON[o.audience] || Briefcase;
                  return (
                    <motion.div key={o.id} data-testid={`opp-card-${o.id}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                      className="bg-white rounded-2xl border border-blue-900/5 shadow-sm p-5 flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${AUD_COLOR[o.audience]}`}><Icon className="w-3 h-3" /> {o.audience.toUpperCase()}</span>
                        <span className="text-[11px] text-slate-500 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {o.deadline}</span>
                      </div>
                      <h3 className="font-display font-semibold text-blue-950 leading-snug">{o.title}</h3>
                      <p className="text-xs text-teal-700 font-medium mt-1">{o.opportunity_type_name}</p>
                      <p className="text-sm text-slate-500 mt-2 leading-relaxed flex-1">{o.description}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4 text-[12px] text-slate-500">
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {o.county ? `${o.county}, ` : ""}{o.state_name}</span>
                        <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> {o.contract_size}</span>
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-blue-900/5">
                        <span className="text-xs text-slate-400">{o.org}</span>
                        <Button asChild size="sm" className="bg-blue-900 hover:bg-blue-950 text-white"><Link to="/request-demo" data-testid={`opp-express-${o.id}`}>Express interest</Link></Button>
                      </div>
                    </motion.div>
                  );
                })}
                {items.length === 0 && <div className="md:col-span-2 text-center py-16 bg-white rounded-2xl border border-blue-900/5"><p className="text-slate-500">No opportunities match these filters.</p></div>}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
